import { CheckCircleFilled } from '@ant-design/icons';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useSafePath } from '@client/hooks/utils/useSafePath';
import { useAuthStore } from '@client/stores/AuthStore';
import { requiredFieldRule } from '@client/utils';
import { AUTH0_CONNECTIONS, type Auth0Connection } from '@shared/constants/system/auth0';
import { createFileRoute } from '@tanstack/react-router';
import { Form, Input, Select, Switch } from 'antd';
import clsx from 'clsx';
import { Building2, Info, Mail, ShieldCheck, Users, Zap } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';

export const Route = createFileRoute('/admin-console/organisation-management/new')({
    staticData: {
        permissions: ['admin_orgs:create'],
        breadcrumb: {
            title: 'Onboard new organisation',
        },
        ui: {
            icon: Building2,
            title: 'Onboard new organisation',
            subtitle: 'Create a new tenant and invite its first administrator.',
            widthVariant: 'narrow',
            menuDescription: 'Create a new tenant organisation',
        },
    },
    component: NewOrganization,
});

type OwnerMode = 'invite' | 'existing';

type State = {
    orgName: string;
    enabledAuth0Connections: Auth0Connection[];
    ownerEmail?: string;
    ownerFirstName?: string;
    ownerLastName?: string;
    existingOwnerEmail?: string;
    supportUserId?: bigint;
    isLiteOrg: boolean;
};

const defaultAuthMethods: Auth0Connection[] = [AUTH0_CONNECTIONS.DATABASE.name, AUTH0_CONNECTIONS.AZURE_AD.name];

const AUTH_METHODS = [
    {
        name: AUTH0_CONNECTIONS.DATABASE.name,
        title: AUTH0_CONNECTIONS.DATABASE.label,
        description: 'Users set a password on sign-up',
        icon: Mail,
    },
    {
        name: AUTH0_CONNECTIONS.AZURE_AD.name,
        title: AUTH0_CONNECTIONS.AZURE_AD.label,
        description: 'Sign in with a work account',
        icon: ShieldCheck,
    },
] as const;

const filterByLabel = (input: string, option?: { label?: ReactNode }) =>
    (option?.label?.toString().toLowerCase() ?? '').includes(input.toLowerCase());

/** Uppercase section heading with an optional status pill. */
function SectionLabel({ children, pill }: { children: ReactNode; pill?: string }) {
    return (
        <div className="mb-3.5 flex items-center gap-2">
            <span className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">{children}</span>
            {pill && (
                <span className="inline-flex h-5 items-center rounded-full bg-muted px-2 font-medium text-muted-foreground text-xs">
                    {pill}
                </span>
            )}
        </div>
    );
}

/** Multi-select of the org's accepted auth methods, rendered as toggleable cards. Controlled by antd Form. */
function AuthMethodToggles({
    value = [],
    onChange,
}: {
    value?: Auth0Connection[];
    onChange?: (value: Auth0Connection[]) => void;
}) {
    const toggle = (name: Auth0Connection) => {
        onChange?.(value.includes(name) ? value.filter((v) => v !== name) : [...value, name]);
    };

    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {AUTH_METHODS.map(({ name, title, description, icon: Icon }) => {
                const selected = value.includes(name);
                return (
                    <button
                        key={name}
                        type="button"
                        onClick={() => toggle(name)}
                        aria-pressed={selected}
                        className={clsx(
                            'relative flex items-start gap-3 rounded-lg border p-3.5 text-left transition-colors',
                            selected
                                ? 'border-accent-500 bg-accent-25'
                                : 'border-separator bg-card hover:border-accent-300',
                        )}
                    >
                        <span
                            className={clsx(
                                'flex size-8 flex-none items-center justify-center rounded-md',
                                selected ? 'bg-accent-100 text-accent-600' : 'bg-muted text-muted-foreground',
                            )}
                        >
                            <Icon className="size-4" />
                        </span>
                        <span className="min-w-0">
                            <span className="block font-semibold text-foreground text-sm">{title}</span>
                            <span className="mt-0.5 block text-muted-foreground text-xs">{description}</span>
                        </span>
                        {selected && <CheckCircleFilled className="absolute top-2.5 right-2.5 text-accent-500" />}
                    </button>
                );
            })}
        </div>
    );
}

function NewOrganization() {
    const reloadUser = useAuthStore((state) => state.reloadUser);
    const navigate = useNavigate();
    const { trpc } = useTrpc();
    const [form] = Form.useForm<State>();
    const safePath = useSafePath<State>();

    const [ownerMode, setOwnerMode] = useState<OwnerMode>('invite');

    // Support accounts for the (optional) support selector.
    const { data: supportAccounts, isLoading: isLoadingSupportAccounts } =
        trpc.admin.supportSession.listSupportAccounts.useQuery();

    // Existing users for the "select existing user" owner mode — only fetched when that mode is active.
    const { data: existingUsers, isLoading: isLoadingUsers } = trpc.admin.user.listUserNames.useQuery(
        {},
        { enabled: ownerMode === 'existing' },
    );

    const supportAccountOptions = useMemo(() => {
        return (
            supportAccounts?.map((account) => {
                const name = [account.firstName, account.lastName].filter(Boolean).join(' ');
                // If name equals email (fallback case), show just email
                const label = name === account.email ? name : `${name} (${account.email})`;
                return { label, value: account.id };
            }) ?? []
        );
    }, [supportAccounts]);

    const existingUserOptions = useMemo(() => {
        return (
            existingUsers?.rows?.map((user) => {
                const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
                const label = name ? `${name} (${user.email})` : user.email;
                return { label, value: user.email };
            }) ?? []
        );
    }, [existingUsers]);

    const { mutateAsync, isPending } = trpc.admin.org.onboardOrg.useMutation({
        async onSuccess() {
            void reloadUser();
            message.success('A new organisation was created.');
            navigate({ to: '/admin-console' });
        },
        onError(error) {
            message.destroy();
            errorMessage.show(error);
        },
        onMutate() {
            message.loading('Creating new organisation...');
        },
    });

    const onFinish = async (values: State) => {
        const ownerEmail = ownerMode === 'existing' ? values.existingOwnerEmail : values.ownerEmail;
        if (!ownerEmail) return; // guarded by field rules; defensive

        await mutateAsync({
            name: values.orgName,
            isLiteOrg: values.isLiteOrg ?? false,
            enabledAuth0Connections: values.enabledAuth0Connections,
            owner: {
                email: ownerEmail,
                firstName: ownerMode === 'invite' ? values.ownerFirstName : undefined,
                lastName: ownerMode === 'invite' ? values.ownerLastName : undefined,
            },
            supportUserId: values.supportUserId,
        });
    };

    return (
        <PageController route={Route}>
            <Form form={form} layout="vertical" onFinish={onFinish} scrollToFirstError>
                <div className="overflow-hidden rounded-lg border border-separator bg-card">
                    <div className="divide-y divide-separator-secondary px-6 sm:px-7">
                        {/* Organisation */}
                        <section className="py-5">
                            <SectionLabel>Organisation</SectionLabel>
                            <Form.Item
                                label="Organisation name"
                                name={safePath('orgName')}
                                rules={requiredFieldRule}
                                className="mb-4"
                            >
                                <Input autoFocus placeholder="e.g. Acme Underwriting" />
                            </Form.Item>

                            <div className="flex items-center gap-3 rounded-lg border border-separator bg-card p-3.5">
                                <span className="flex size-9 flex-none items-center justify-center rounded-md bg-warning-subtle text-warning">
                                    <Zap className="size-4.5" />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="font-semibold text-foreground text-sm">Curium Lite</div>
                                    <div className="mt-0.5 text-muted-foreground text-xs">
                                        Trial tier — only the compliance module is enabled; premium features stay behind
                                        upgrade prompts.
                                    </div>
                                </div>
                                <Form.Item
                                    name={safePath('isLiteOrg')}
                                    valuePropName="checked"
                                    initialValue={false}
                                    noStyle
                                >
                                    <Switch aria-label="Enable Curium Lite" />
                                </Form.Item>
                            </div>
                        </section>

                        {/* Authentication methods */}
                        <section className="py-5">
                            <SectionLabel>Authentication methods</SectionLabel>
                            <p className="-mt-2 mb-3.5 text-sm text-subtle-foreground">
                                Sign-in methods the organisation accepts. The owner inherits these.
                            </p>
                            <Form.Item
                                name={safePath('enabledAuth0Connections')}
                                initialValue={defaultAuthMethods}
                                rules={[
                                    {
                                        required: true,
                                        type: 'array',
                                        min: 1,
                                        message: 'Select at least one authentication method',
                                    },
                                ]}
                                className="mb-0"
                            >
                                <AuthMethodToggles />
                            </Form.Item>
                        </section>

                        {/* Owner */}
                        <section className="py-5">
                            <SectionLabel pill="Required">Owner</SectionLabel>
                            <div className="mb-4">
                                <SegmentedControl<OwnerMode>
                                    value={ownerMode}
                                    onChange={setOwnerMode}
                                    block
                                    size="middle"
                                    aria-label="Owner provisioning mode"
                                    options={[
                                        {
                                            value: 'invite',
                                            label: 'Invite by email',
                                            icon: <Mail className="size-4" />,
                                        },
                                        {
                                            value: 'existing',
                                            label: 'Select existing user',
                                            icon: <Users className="size-4" />,
                                        },
                                    ]}
                                />
                            </div>

                            {ownerMode === 'invite' ? (
                                <>
                                    <Form.Item
                                        label="Owner email"
                                        name={safePath('ownerEmail')}
                                        rules={[
                                            { required: true, message: 'Owner email is required' },
                                            { type: 'email', message: 'Enter a valid email address' },
                                        ]}
                                        className="mb-4"
                                    >
                                        <Input placeholder="owner@acme.com" />
                                    </Form.Item>
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                        <Form.Item
                                            label="First name"
                                            name={safePath('ownerFirstName')}
                                            className="mb-0"
                                        >
                                            <Input placeholder="First name" />
                                        </Form.Item>
                                        <Form.Item label="Last name" name={safePath('ownerLastName')} className="mb-0">
                                            <Input placeholder="Last name" />
                                        </Form.Item>
                                    </div>
                                    <p className="mt-2.5 text-muted-foreground text-xs">
                                        They receive an invite link and become the organisation's first admin.
                                    </p>
                                </>
                            ) : (
                                <Form.Item
                                    label="Existing user"
                                    name={safePath('existingOwnerEmail')}
                                    rules={[{ required: true, message: 'Select an existing user' }]}
                                    extra="Attach someone who already has a Curium account as the owner."
                                    className="mb-0"
                                >
                                    <Select
                                        showSearch
                                        placeholder="Search users by name or email"
                                        options={existingUserOptions}
                                        loading={isLoadingUsers}
                                        filterOption={filterByLabel}
                                    />
                                </Form.Item>
                            )}
                        </section>

                        {/* Support account */}
                        <section className="py-5">
                            <SectionLabel pill="Optional">Support account</SectionLabel>
                            <Form.Item
                                name={safePath('supportUserId')}
                                extra="If set, this Curium support account gets org admin access via a 6-month auto-approved session."
                                className="mb-0"
                            >
                                <Select
                                    allowClear
                                    showSearch
                                    placeholder="No support account"
                                    options={supportAccountOptions}
                                    loading={isLoadingSupportAccounts}
                                    filterOption={filterByLabel}
                                />
                            </Form.Item>
                        </section>
                    </div>

                    <div className="flex items-center justify-between gap-4 border-separator border-t bg-card-header-bg px-6 py-4 sm:px-7">
                        <span className="flex items-center gap-1.5 text-muted-foreground text-xs">
                            <Info className="size-3.5 flex-none text-subtle-foreground" />
                            The owner is always required.
                        </span>
                        <Space>
                            <Button onClick={() => navigate({ to: '/admin-console' })}>Cancel</Button>
                            <Button type="primary" htmlType="submit" loading={isPending}>
                                Create organisation
                            </Button>
                        </Space>
                    </div>
                </div>
            </Form>
        </PageController>
    );
}
